/**
 * Workflow Template Gallery
 *
 * Displays available system workflow templates for selection.
 * Loads from DynamoDB with no hardcoded workflows.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Card,
  Text,
  Button,
  Badge,
  Group,
  Stack,
  Title,
  Select,
  TextInput,
  LoadingOverlay,
  Alert,
  Image,
  ActionIcon,
  Tooltip,
  Paper,
  Divider,
  SimpleGrid,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconInfoCircle,
  IconClock,
  IconUsers,
  IconShield,
  IconFileText,
  IconSettings,
  IconTrendingUp,
  IconBellRinging,
  IconDatabase,
  IconBuildingBank,
  IconUser,
} from '@tabler/icons-react';

import {
  WorkflowTemplate,
  WorkflowCategory,
  WorkflowTemplateService,
  workflowUtils,
} from '../templates/SystemWorkflowTemplates';

export interface WorkflowTemplateGalleryProps {
  onSelectTemplate: (template: WorkflowTemplate) => void;
  tenantId: string;
  userRoles: string[];
  showCategoryFilter?: boolean;
  showSearch?: boolean;
  showComplianceFilter?: boolean;
  itemsPerRow?: number;
}

export const WorkflowTemplateGallery: React.FC<WorkflowTemplateGalleryProps> = ({
  onSelectTemplate,
  tenantId,
  userRoles,
  showCategoryFilter = true,
  showSearch = true,
  showComplianceFilter = true,
  itemsPerRow = 3,
}) => {
  // State management
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<WorkflowCategory | ''>('');
  const [selectedCompliance, setSelectedCompliance] = useState<string>('');

  // Services
  const templateService = useMemo(() => new WorkflowTemplateService(), []);

  // Load workflow templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        setError(null);

        const availableTemplates = await templateService.getAvailableWorkflows(tenantId);

        // Filter templates based on user roles
        const accessibleTemplates = availableTemplates.filter(template =>
          workflowUtils.hasRequiredRoles(userRoles, template)
        );

        setTemplates(accessibleTemplates);
      } catch (err) {
        console.error('Failed to load workflow templates:', err);
        setError(err instanceof Error ? err.message : 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [templateService, userRoles]);

  // Filter templates based on search and filters
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          template.display_name.toLowerCase().includes(query) ||
          template.description.toLowerCase().includes(query) ||
          template.template_name.toLowerCase().includes(query);

        if (!matchesSearch) return false;
      }

      // Category filter
      if (selectedCategory && template.category !== selectedCategory) {
        return false;
      }

      // Compliance filter
      if (selectedCompliance && !template.compliance_frameworks.includes(selectedCompliance)) {
        return false;
      }

      return true;
    });
  }, [templates, searchQuery, selectedCategory, selectedCompliance]);

  // Get unique compliance frameworks
  const complianceFrameworks = useMemo(() => {
    const frameworks = new Set<string>();
    templates.forEach(template => {
      template.compliance_frameworks.forEach(framework => {
        frameworks.add(framework);
      });
    });
    return Array.from(frameworks).sort();
  }, [templates]);

  // Category options for filter
  const categoryOptions = useMemo(() => {
    return Object.values(WorkflowCategory).map(category => ({
      value: category,
      label: workflowUtils.getCategoryDisplayName(category),
    }));
  }, []);

  // Get category icon
  const getCategoryIcon = (category: WorkflowCategory) => {
    const iconProps = { size: 20 };

    switch (category) {
      case WorkflowCategory.UserManagement:
        return <IconUser {...iconProps} />;
      case WorkflowCategory.DocumentApproval:
        return <IconFileText {...iconProps} />;
      case WorkflowCategory.ComplianceReview:
        return <IconShield {...iconProps} />;
      case WorkflowCategory.SystemIntegration:
        return <IconSettings {...iconProps} />;
      case WorkflowCategory.DataProcessing:
        return <IconDatabase {...iconProps} />;
      case WorkflowCategory.SecurityIncident:
        return <IconBellRinging {...iconProps} />;
      case WorkflowCategory.TenantOnboarding:
        return <IconBuildingBank {...iconProps} />;
      case WorkflowCategory.PolicyManagement:
        return <IconShield {...iconProps} />;
      case WorkflowCategory.AuditProcess:
        return <IconTrendingUp {...iconProps} />;
      case WorkflowCategory.BusinessProcess:
        return <IconSettings {...iconProps} />;
      default:
        return <IconFileText {...iconProps} />;
    }
  };

  // Get complexity badge color
  const getComplexityColor = (parameterCount: number): string => {
    if (parameterCount <= 3) return 'green';
    if (parameterCount <= 6) return 'yellow';
    return 'red';
  };

  // Get complexity label
  const getComplexityLabel = (parameterCount: number): string => {
    if (parameterCount <= 3) return 'Simple';
    if (parameterCount <= 6) return 'Medium';
    return 'Complex';
  };

  // Render template card
  const renderTemplateCard = (template: WorkflowTemplate) => {
    const complexityColor = getComplexityColor(template.parameters.length);
    const complexityLabel = getComplexityLabel(template.parameters.length);
    const requiredCompliance = workflowUtils.getRequiredComplianceFrameworks(template);

    return (
      <Card key={template.id} shadow="sm" padding="lg" radius="md" withBorder>
        <Card.Section withBorder inheritPadding py="xs">
          <Group justify="space-between">
            <Group gap="xs">
              {getCategoryIcon(template.category)}
              <Text fw={500}>{template.display_name}</Text>
            </Group>
            <Badge color={complexityColor} variant="light" size="sm">
              {complexityLabel}
            </Badge>
          </Group>
        </Card.Section>

        <Text size="sm" c="dimmed" mt="sm" style={{ minHeight: 60 }}>
          {template.description}
        </Text>

        <Stack gap="xs" mt="md">
          {/* Category */}
          <Group justify="space-between">
            <Text size="xs" c="dimmed">Category:</Text>
            <Badge variant="outline" size="xs">
              {workflowUtils.getCategoryDisplayName(template.category)}
            </Badge>
          </Group>

          {/* Field count */}
          <Group justify="space-between">
            <Text size="xs" c="dimmed">Fields:</Text>
            <Text size="xs">{template.parameters.length} parameters</Text>
          </Group>

          {/* Compliance frameworks */}
          {requiredCompliance.length > 0 && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">Compliance:</Text>
              <Group gap={4}>
                {requiredCompliance.map(framework => (
                  <Badge key={framework} size="xs" variant="dot" color="blue">
                    {framework}
                  </Badge>
                ))}
              </Group>
            </Group>
          )}

          {/* Version */}
          <Group justify="space-between">
            <Text size="xs" c="dimmed">Version:</Text>
            <Text size="xs">{template.version}</Text>
          </Group>
        </Stack>

        <Divider my="sm" />

        <Group justify="space-between">
          <Tooltip label="View template details">
            <ActionIcon variant="subtle" size="sm">
              <IconInfoCircle size={16} />
            </ActionIcon>
          </Tooltip>

          <Button
            variant="filled"
            size="sm"
            onClick={() => onSelectTemplate(template)}
          >
            Use Template
          </Button>
        </Group>
      </Card>
    );
  };

  // Loading state
  if (loading) {
    return (
      <Paper p="xl" withBorder>
        <LoadingOverlay visible />
        <Stack ta="center" gap="md" style={{ minHeight: 200 }}>
          <Text>Loading workflow templates...</Text>
        </Stack>
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert icon={<IconBellRinging size={16} />} title="Error Loading Templates" color="red">
        {error}
        <Button
          variant="outline"
          size="xs"
          mt="sm"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <div>
        <Title order={2} mb="xs">Workflow Templates</Title>
        <Text c="dimmed">
          Choose from {templates.length} available system workflows. All templates are dynamically loaded and can be customized for your tenant.
        </Text>
      </div>

      {/* Filters */}
      <Paper p="md" withBorder>
        <Grid>
          {showSearch && (
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <TextInput
                placeholder="Search templates..."
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(event.currentTarget.value)}
              />
            </Grid.Col>
          )}

          {showCategoryFilter && (
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Select
                placeholder="All categories"
                leftSection={<IconFilter size={16} />}
                data={[
                  { value: '', label: 'All Categories' },
                  ...categoryOptions,
                ]}
                value={selectedCategory}
                onChange={(value: string | null) => setSelectedCategory(value as WorkflowCategory | '')}
                clearable
              />
            </Grid.Col>
          )}

          {showComplianceFilter && complianceFrameworks.length > 0 && (
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Select
                placeholder="All compliance"
                leftSection={<IconShield size={16} />}
                data={[
                  { value: '', label: 'All Compliance' },
                  ...complianceFrameworks.map(framework => ({
                    value: framework,
                    label: framework,
                  })),
                ]}
                value={selectedCompliance}
                onChange={(value: string | null) => setSelectedCompliance(value || '')}
                clearable
              />
            </Grid.Col>
          )}
        </Grid>
      </Paper>

      {/* Results count */}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Showing {filteredTemplates.length} of {templates.length} templates
        </Text>
        {filteredTemplates.length > 0 && (
          <Text size="sm" c="dimmed">
            {Math.ceil(filteredTemplates.length / itemsPerRow)} rows
          </Text>
        )}
      </Group>

      {/* Template grid */}
      {filteredTemplates.length > 0 ? (
        <SimpleGrid
          cols={{ base: 1, sm: 2, md: itemsPerRow }}
          spacing="lg"
        >
          {filteredTemplates.map(renderTemplateCard)}
        </SimpleGrid>
      ) : (
        <Paper p="xl" withBorder>
          <Stack ta="center" gap="md">
            <IconFileText size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />
            <div style={{ textAlign: 'center' }}>
              <Text fw={500} mb="xs">No templates found</Text>
              <Text size="sm" c="dimmed">
                {searchQuery || selectedCategory || selectedCompliance
                  ? 'Try adjusting your filters to see more results.'
                  : 'No workflow templates are available for your user roles.'
                }
              </Text>
            </div>
            {(searchQuery || selectedCategory || selectedCompliance) && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('');
                  setSelectedCompliance('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {/* Summary stats */}
      {templates.length > 0 && (
        <Paper p="md" withBorder>
          <Group justify="space-around">
            <Stack ta="center" gap={0}>
              <Text size="lg" fw={500}>{templates.length}</Text>
              <Text size="xs" c="dimmed">Total Templates</Text>
            </Stack>
            <Stack ta="center" gap={0}>
              <Text size="lg" fw={500}>
                {new Set(templates.map(t => t.category)).size}
              </Text>
              <Text size="xs" c="dimmed">Categories</Text>
            </Stack>
            <Stack ta="center" gap={0}>
              <Text size="lg" fw={500}>
                {new Set(templates.flatMap(t => t.compliance_frameworks)).size}
              </Text>
              <Text size="xs" c="dimmed">Compliance Types</Text>
            </Stack>
            <Stack ta="center" gap={0}>
              <Text size="lg" fw={500}>
                {templates.filter(t => t.is_system_default).length}
              </Text>
              <Text size="xs" c="dimmed">System Defaults</Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Stack>
  );
};

export default WorkflowTemplateGallery;